import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle } from 'lucide-react';
// Trustpilot-style star: white star inside a green rounded square
function TrustpilotStar({
  size = 20
}: {
  size?: number;
}) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-596">
      <rect width="24" height="24" rx="3" fill="#00B67A" data-id="element-597" />
      <path d="M12 4L14.09 9.26L20 9.27L15.45 12.97L17.18 18L12 15.27L6.82 18L8.55 12.97L4 9.27L9.91 9.26L12 4Z" fill="white" data-id="element-598" />
    </svg>;
}
function StarRating({
  count = 5,
  size = 20
}: {
  count?: number;
  size?: number;
}) {
  return <div className="flex gap-0.5" data-id="element-599">
      {[...Array(count)].map((_, i) => <TrustpilotStar key={i} size={size} data-id="element-600" />)}
    </div>;
}
function Avatar({
  name,
  variant
}: {
  name: string;
  variant: 'green' | 'amber';
}) {
  const initial = name.charAt(0).toUpperCase();
  const bgColor = variant === 'green' ? 'bg-[#E8F5EE] text-[#2D5A3D]' : 'bg-[#FEF3E2] text-[#C17817]';
  return <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${bgColor}`} data-id="element-601">
      {initial}
    </div>;
}
export function TestimonialsSection() {
  const reviews = [{
    name: 'Carmen R.',
    age: '71 años',
    result: 'Memoria y Concentración',
    highlight: 'Empecé a recordar nombres, fechas y hasta sueños que no recordaba desde hace años.',
    text: 'Mi hijo pensó que estaba exagerando hasta que me vio resolverle un crucigrama en 10 minutos. El protocolo "Cerebro de Cristal" cambió mi vida. Lo tomo cada mañana a las 7 AM como dice el manual.',
    stars: 5,
    date: 'hace 2 semanas',
    avatarVariant: 'green' as const
  }, {
    name: 'Roberto M.',
    age: '64 años',
    result: 'Presión Arterial',
    highlight: 'Mi presión bajó de 160/95 a 128/82 en 6 semanas.',
    text: 'Mi cardiólogo me preguntó qué había cambiado. Ahora estamos reduciendo la dosis de mis medicamentos juntos. El tónico "Corazón de Atleta" es lo más poderoso que he probado.',
    stars: 5,
    date: 'hace 1 mes',
    avatarVariant: 'amber' as const
  }, {
    name: 'Marta L.',
    age: '67 años',
    result: 'Dolor Articular',
    highlight: 'Pude subir escaleras sin agarrarme del pasamanos por primera vez en 6 años.',
    text: 'Después de 3 semanas con el protocolo "Fuego Apagado" noté la diferencia. Mi reumatólogo está sorprendido. Solo son ingredientes de cocina — no puedo creer que nadie me lo hubiera dicho antes.',
    stars: 5,
    date: 'hace 3 semanas',
    avatarVariant: 'green' as const
  }, {
    name: 'Isabelle C.',
    age: '52 años',
    role: 'Enfermera Certificada',
    result: 'Sueño y Hormonas',
    highlight: 'Soy enfermera y era escéptica. Los protocolos tienen respaldo científico real.',
    text: 'El "Arquitecto del Sueño" cambió completamente mi ciclo. Duermo profundo por primera vez en años. Y la "Armonía Hormonal" eliminó mis sofocos en 2 semanas. Lo recomiendo a todas mis pacientes.',
    stars: 5,
    date: 'hace 6 semanas',
    avatarVariant: 'amber' as const
  }];
  return <section className="py-16 px-4 bg-[#F0F4EC] border-t border-[#E8DFD0]" data-id="element-602">
      <div className="max-w-[850px] mx-auto" data-id="element-603">
        {/* Trustpilot-style Header */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-10" data-id="element-604">
          {/* Verified Badge */}
          <div className="inline-flex items-center gap-1.5 bg-[#E8F5EE] text-[#00B67A] px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4" data-id="element-605">
            <CheckCircle className="w-3.5 h-3.5" data-id="element-606" />
            Reseñas Verificadas
          </div>

          {/* Rating Summary */}
          <div className="flex flex-col items-center gap-3" data-id="element-607">
            <div className="flex items-center gap-3" data-id="element-608">
              <span className="text-5xl font-bold text-[#2C1810]" data-id="element-609">4.9</span>
              <div className="text-left" data-id="element-610">
                <p className="text-[#00B67A] font-bold text-lg" data-id="element-611">Excelente</p>
                <StarRating count={5} size={22} data-id="element-612" />
              </div>
            </div>
            <p className="text-sm text-[#5C4A3A]" data-id="element-613">
              Basado en <span className="font-semibold" data-id="element-614">847 reseñas</span>{' '}
              verificadas
            </p>
          </div>
        </motion.div>

        {/* Reviews Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10" data-id="element-615">
          {reviews.map((review, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="bg-white rounded-xl p-5 border border-[#EEEEEE] shadow-[0_2px_8px_rgba(0,0,0,0.04)] relative" data-id="element-616">
              {/* Result Tag */}
              <div className="absolute top-4 right-4" data-id="element-617">
                <span className="bg-[#F0F4EC] text-[#2D5A3D] text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border border-[#2D5A3D]/10" data-id="element-618">
                  {review.result}
                </span>
              </div>

              {/* Stars */}
              <div className="mb-3" data-id="element-619">
                <StarRating count={review.stars} size={18} data-id="element-620" />
              </div>

              {/* Headline */}
              <h4 className="font-bold text-[#2C1810] text-base mb-2 pr-24 leading-snug" data-id="element-621">
                {review.highlight}
              </h4>

              {/* Body */}
              <p className="text-[#5C4A3A] text-sm leading-relaxed mb-4" data-id="element-622">
                {review.text}
              </p>

              {/* Footer */}
              <div className="flex items-center justify-between pt-3 border-t border-[#F5F5F5]" data-id="element-623">
                <div className="flex items-center gap-3" data-id="element-624">
                  <Avatar name={review.name} variant={review.avatarVariant} data-id="element-625" />
                  <div data-id="element-626">
                    <p className="font-bold text-[#2C1810] text-sm" data-id="element-627">
                      {review.name}
                    </p>
                    <div className="flex items-center gap-1 text-[10px] text-[#00B67A] font-medium" data-id="element-628">
                      <CheckCircle className="w-3 h-3" data-id="element-629" />
                      <span data-id="element-630">Compra verificada</span>
                    </div>
                  </div>
                </div>
                <span className="text-xs text-[#8B7355]" data-id="element-631">{review.date}</span>
              </div>
            </motion.div>)}
        </div>

        {/* CTA Card */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-white border border-[#E8DFD0] rounded-xl p-6 shadow-sm text-center" data-id="element-632">
          <p className="text-[#5C4A3A] text-sm mb-1" data-id="element-633">
            ¿Cuánto tiempo más vas a esperar?
          </p>
          <p className="font-serif font-bold text-[#2C1810] text-xl mb-4" data-id="element-634">
            Estas personas tomaron la decisión.{' '}
            <span className="text-[#2D5A3D]" data-id="element-635">Tú puedes ser el próximo.</span>
          </p>
          <a href="#comprar" className="inline-block bg-gradient-to-r from-[#2D5A3D] to-[#1B4332] text-white font-bold py-3 px-8 rounded-xl text-sm uppercase tracking-wide shadow-lg hover:-translate-y-0.5 transition-transform" data-id="element-636">
            Quiero Mis Resultados →
          </a>
        </motion.div>
      </div>
    </section>;
}